/**
 * HRM API Service
 * Handles all API calls to the HRM microservice
 */

import api from './api';

const HRM_BASE_URL = '/hrm';

// ============================================================================
// ORGANIZATION MANAGEMENT
// ============================================================================
// NOTE: Companies have been migrated to Locations in Admin Service.
// Use adminAPI.getLocations() instead of organizationApi.getCompanies()

export const organizationApi = {
  // Divisions
  getDivisions: (params = {}) => api.get(`${HRM_BASE_URL}/divisions`, { params }),
  getDivisionsDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/divisions/dropdown`, { params }),
  getDivisionById: (id) => api.get(`${HRM_BASE_URL}/divisions/${id}`),
  createDivision: (data) => api.post(`${HRM_BASE_URL}/divisions`, data),
  updateDivision: (id, data) => api.put(`${HRM_BASE_URL}/divisions/${id}`, data),
  deleteDivision: (id) => api.delete(`${HRM_BASE_URL}/divisions/${id}`),

  // Departments
  getDepartments: (params = {}) => api.get(`${HRM_BASE_URL}/departments`, { params }),
  getDepartmentsDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/departments/dropdown`, { params }),
  getDepartmentById: (id) => api.get(`${HRM_BASE_URL}/departments/${id}`),
  createDepartment: (data) => api.post(`${HRM_BASE_URL}/departments`, data),
  updateDepartment: (id, data) => api.put(`${HRM_BASE_URL}/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`${HRM_BASE_URL}/departments/${id}`),

  // Sections
  getSections: (params = {}) => api.get(`${HRM_BASE_URL}/sections`, { params }),
  getSectionsDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/sections/dropdown`, { params }),
  getSectionById: (id) => api.get(`${HRM_BASE_URL}/sections/${id}`),
  createSection: (data) => api.post(`${HRM_BASE_URL}/sections`, data),
  updateSection: (id, data) => api.put(`${HRM_BASE_URL}/sections/${id}`, data),
  deleteSection: (id) => api.delete(`${HRM_BASE_URL}/sections/${id}`),

  // Units
  getUnits: (params = {}) => api.get(`${HRM_BASE_URL}/units`, { params }),
  getUnitsDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/units/dropdown`, { params }),
  getUnitById: (id) => api.get(`${HRM_BASE_URL}/units/${id}`),
  createUnit: (data) => api.post(`${HRM_BASE_URL}/units`, data),
  updateUnit: (id, data) => api.put(`${HRM_BASE_URL}/units/${id}`, data),
  deleteUnit: (id) => api.delete(`${HRM_BASE_URL}/units/${id}`),

  // Job Families
  getJobFamilies: (params = {}) => api.get(`${HRM_BASE_URL}/job-families`, { params }),
  getJobFamiliesDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/job-families/dropdown`, { params }),
  getJobFamilyById: (id) => api.get(`${HRM_BASE_URL}/job-families/${id}`),
  createJobFamily: (data) => api.post(`${HRM_BASE_URL}/job-families`, data),
  updateJobFamily: (id, data) => api.put(`${HRM_BASE_URL}/job-families/${id}`, data),
  deleteJobFamily: (id) => api.delete(`${HRM_BASE_URL}/job-families/${id}`),

  // Designations (backend routes still use /positions, field names: designationName, designationCode)
  getPositions: (params = {}) => api.get(`${HRM_BASE_URL}/positions`, { params }),
  getPositionsDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/positions/dropdown`, { params }),
  getPositionById: (id) => api.get(`${HRM_BASE_URL}/positions/${id}`),
  createPosition: (data) => api.post(`${HRM_BASE_URL}/positions`, data),
  updatePosition: (id, data) => api.put(`${HRM_BASE_URL}/positions/${id}`, data),
  deletePosition: (id) => api.delete(`${HRM_BASE_URL}/positions/${id}`),

  // Grades
  getGrades: (params = {}) => api.get(`${HRM_BASE_URL}/grades`, { params }),
  getGradesDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/grades/dropdown`, { params }),
  getGradeById: (id) => api.get(`${HRM_BASE_URL}/grades/${id}`),
  createGrade: (data) => api.post(`${HRM_BASE_URL}/grades`, data),
  updateGrade: (id, data) => api.put(`${HRM_BASE_URL}/grades/${id}`, data),
  deleteGrade: (id) => api.delete(`${HRM_BASE_URL}/grades/${id}`),

  // Cost Centers
  getCostCenters: (params = {}) => api.get(`${HRM_BASE_URL}/cost-centers`, { params }),
  getCostCenterById: (id) => api.get(`${HRM_BASE_URL}/cost-centers/${id}`),
  createCostCenter: (data) => api.post(`${HRM_BASE_URL}/cost-centers`, data),
  updateCostCenter: (id, data) => api.put(`${HRM_BASE_URL}/cost-centers/${id}`, data),
  deleteCostCenter: (id) => api.delete(`${HRM_BASE_URL}/cost-centers/${id}`),

  // Org Chart
  getOrgChart: (params = {}) => api.get(`${HRM_BASE_URL}/org-chart`, { params }),

  // Headcount Planning
  getHeadcountPlanning: (params = {}) => api.get(`${HRM_BASE_URL}/headcount`, { params }),
  getHeadcountPlans: (params = {}) => api.get(`${HRM_BASE_URL}/headcount`, { params }),
  getHeadcountPlanById: (id) => api.get(`${HRM_BASE_URL}/headcount/${id}`),
  createHeadcountPlan: (data) => api.post(`${HRM_BASE_URL}/headcount`, data),
  updateHeadcountPlan: (id, data) => api.put(`${HRM_BASE_URL}/headcount/${id}`, data),
  deleteHeadcountPlan: (id) => api.delete(`${HRM_BASE_URL}/headcount/${id}`),
};

// ============================================================================
// EMPLOYEE MANAGEMENT
// ============================================================================

export const employeeApi = {
  // Employee CRUD
  getEmployees: (params = {}) => api.get(`${HRM_BASE_URL}/employees`, { params }),
  getEmployeesDropdown: (params = {}) => api.get(`${HRM_BASE_URL}/employees/dropdown`, { params }),
  getEmployeeById: (id) => api.get(`${HRM_BASE_URL}/employees/${id}`),
  createEmployee: (data) => api.post(`${HRM_BASE_URL}/employees`, data),
  updateEmployee: (id, data) => api.put(`${HRM_BASE_URL}/employees/${id}`, data),
  approveProfilePhoto: (id) => api.put(`${HRM_BASE_URL}/employees/${id}/approve-photo`),
  rejectProfilePhoto: (id) => api.put(`${HRM_BASE_URL}/employees/${id}/reject-photo`),

  // Get employee by email
  getEmployeeByEmail: (email) => api.get(`${HRM_BASE_URL}/employees/by-email/${encodeURIComponent(email)}`),

  // Employee Details
  getEmployeeProfile: (id) => api.get(`${HRM_BASE_URL}/employees/${id}/profile`),
  getEmployeeDocuments: (id) => api.get(`${HRM_BASE_URL}/employees/${id}/documents`),
  uploadEmployeeDocument: (id, formData) => api.post(`${HRM_BASE_URL}/employees/${id}/documents`, formData),
  linkEmployeeDocument: (id, data) => api.post(`${HRM_BASE_URL}/employees/${id}/documents/link`, data),
  deleteEmployeeDocument: (employeeId, documentId) => api.delete(`${HRM_BASE_URL}/employees/${employeeId}/documents/${documentId}`),
  updateEmployeeDocument: (employeeId, documentId, data) => api.put(`${HRM_BASE_URL}/employees/${employeeId}/documents/${documentId}`, data),

  // Employee Hierarchy
  getEmployeeHierarchy: (id) => api.get(`${HRM_BASE_URL}/employees/${id}/hierarchy`),
  getReportingEmployees: (managerId) => api.get(`${HRM_BASE_URL}/employees/manager/${managerId}/reports`),

  // Employee Actions
  promoteEmployee: (id, data) => api.post(`${HRM_BASE_URL}/employees/${id}/promote`, data),
  transferEmployee: (id, data) => api.post(`${HRM_BASE_URL}/employees/${id}/transfer`, data),
  terminateEmployee: (id, data) => api.post(`${HRM_BASE_URL}/employees/${id}/terminate`, data),
  confirmEmployee: (id, data) => api.post(`${HRM_BASE_URL}/employees/${id}/confirm`, data),

  // Employee Statistics & Export
  getEmployeeStats: (params = {}) => api.get(`${HRM_BASE_URL}/employees/stats`, { params }),
  getEmployeeDashboard: () => api.get(`${HRM_BASE_URL}/employees/dashboard`),
  exportEmployees: (params = {}) => api.get(`${HRM_BASE_URL}/employees/export`, { params, responseType: 'blob' }),

  // Employee User Creation
  checkEmployeeEmailExists: (email) => api.get(`${HRM_BASE_URL}/employees/check-employee-email/${encodeURIComponent(email)}`),
  checkUserEmailExists: (email) => api.get(`${HRM_BASE_URL}/employees/check-user-email/${encodeURIComponent(email)}`),
  checkNICExists: (nic) => api.get(`${HRM_BASE_URL}/employees/check-nic/${encodeURIComponent(nic)}`),
  checkEmployeeIdExists: (employeeId, excludeId = null) => {
    const params = excludeId ? { excludeId } : {};
    return api.get(`${HRM_BASE_URL}/employees/check-employeeno/${encodeURIComponent(employeeId)}`, { params });
  },
  createEmployeeWithUser: (data) => api.post(`${HRM_BASE_URL}/employees/with-user`, data),
  generateEmployeeId: () => api.get(`${HRM_BASE_URL}/employees/generate-id`),
  getEmployeesEligibleForSeparation: () => api.get(`${HRM_BASE_URL}/employees/eligible-for-separation`),
  // Special endpoint for payroll with bypassed permissions
  getEmployeesForPayroll: (params = {}) => api.get(`${HRM_BASE_URL}/employees/payroll-list`, { params: { ...params, limit: 1000 } }),

  // Bulk Import
  getImportTemplateData: () => api.get(`${HRM_BASE_URL}/employees/import/template-data`),
  previewImport: (data) => api.post(`${HRM_BASE_URL}/employees/import/preview`, { data }),
  confirmImport: (employees) => api.post(`${HRM_BASE_URL}/employees/import/confirm`, { employees }),

  // Alias for backward compatibility
  getAllEmployees: (params = {}) => api.get(`${HRM_BASE_URL}/employees`, { params: { ...params, limit: 1000 } }),
};

// ============================================================================
// ATTENDANCE MANAGEMENT
// ============================================================================

export const attendanceApi = {
  // Attendance Records
  getAttendance: (params = {}) => api.get(`${HRM_BASE_URL}/attendance`, { params }),
  getAllAttendance: (params = {}) => api.get(`${HRM_BASE_URL}/attendance`, { params }), // Alias
  getAttendanceRecords: (params = {}) => api.get(`${HRM_BASE_URL}/attendance`, { params }),
  importMDB: (data) => api.post(`${HRM_BASE_URL}/attendance/import-mdb`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  syncMDB: () => api.post(`${HRM_BASE_URL}/attendance/sync`),
  syncToAllLogs: () => api.post(`${HRM_BASE_URL}/attendance/sync-to-all-logs`),
  getAttendanceById: (id) => api.get(`${HRM_BASE_URL}/attendance/${id}`),
  markAttendance: (data) => api.post(`${HRM_BASE_URL}/attendance`, data),
  updateAttendance: (id, data) => api.put(`${HRM_BASE_URL}/attendance/${id}`, data),
  deleteAttendance: (id) => api.delete(`${HRM_BASE_URL}/attendance/${id}`),
  getTodayStatus: (employeeId) => api.get(`${HRM_BASE_URL}/attendance/status/${employeeId}`),

  // Employee Attendance History
  getEmployeeAttendanceHistory: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/employee-history`, { params }),
  // Self Attendance History
  getSelfAttendanceHistory: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/self-history`, { params }),
  downloadSelfAttendanceCSV: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/self-history/export`, { params, responseType: 'blob' }),
  downloadSelfAttendancePDF: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/self-history/export-pdf`, { params, responseType: 'blob' }),
  downloadEmployeeAttendanceCSV: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/employee-history/export`, { params, responseType: 'blob' }),
  downloadEmployeeAttendancePDF: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/employee-history/export-pdf`, { params, responseType: 'blob' }),

  // Check-in/Check-out
  checkIn: (data) => api.post(`${HRM_BASE_URL}/attendance/check-in`, data),
  checkOut: (data) => api.post(`${HRM_BASE_URL}/attendance/check-out`, data),
  sendAttendanceAlert: (data) => api.post(`${HRM_BASE_URL}/attendance/send-alert`, data),

  // Attendance Reports
  getAttendanceReport: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/report`, { params }),
  getAttendanceSummary: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/summary`, { params }),
  exportAttendance: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/export`, { params, responseType: 'blob' }),
  getAttendanceDashboard: () => api.get(`${HRM_BASE_URL}/attendance-dashboard`),

  // Attendance Requests
  createAttendanceRequest: (data) => api.post(`${HRM_BASE_URL}/attendance/requests`, data),
  getMyAttendanceRequests: (params) => api.get(`${HRM_BASE_URL}/attendance/requests/my`, { params }),
  getAllAttendanceRequests: (params) => api.get(`${HRM_BASE_URL}/attendance/requests/all`, { params }),
  approveAttendanceRequest: (id, data) => api.post(`${HRM_BASE_URL}/attendance/requests/${id}/approve`, data),
  rejectAttendanceRequest: (id, data) => api.post(`${HRM_BASE_URL}/attendance/requests/${id}/reject`, data),
  updateAttendanceRequest: (id, data) => api.put(`${HRM_BASE_URL}/attendance/requests/${id}`, data),
  deleteAttendanceRequest: (id) => api.delete(`${HRM_BASE_URL}/attendance/requests/${id}`),

  // HOD Attendance Approval
  getHODPendingRequests: (params) => api.get(`${HRM_BASE_URL}/attendance/hod/pending`, { params }),
  approveHODRequest: (id, data) => api.post(`${HRM_BASE_URL}/attendance/hod/requests/${id}/approve`, data),
  rejectHODRequest: (id, data) => api.post(`${HRM_BASE_URL}/attendance/hod/requests/${id}/reject`, data),

  // Supervisor Attendance Approval
  getSupervisorAttendanceRequests: (params) => api.get(`${HRM_BASE_URL}/attendance/supervisor/requests`, { params }),
  approveSupervisorAttendanceRequest: (id, data) => api.post(`${HRM_BASE_URL}/attendance/supervisor/requests/${id}/approve`, data),
  rejectSupervisorAttendanceRequest: (id, data) => api.post(`${HRM_BASE_URL}/attendance/supervisor/requests/${id}/reject`, data),
  getSupervisorAttendanceStatistics: () => api.get(`${HRM_BASE_URL}/attendance/supervisor/statistics`),

  // Shift Management
  getShifts: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/shifts`, { params }),
  getShiftById: (id) => api.get(`${HRM_BASE_URL}/attendance/shifts/${id}`),
  createShift: (data) => api.post(`${HRM_BASE_URL}/attendance/shifts`, data),
  updateShift: (id, data) => api.put(`${HRM_BASE_URL}/attendance/shifts/${id}`, data),
  deleteShift: (id) => api.delete(`${HRM_BASE_URL}/attendance/shifts/${id}`),
  assignShift: (data) => api.post(`${HRM_BASE_URL}/attendance/shifts/assign`, data),

  // Settings
  getSettings: () => api.get(`${HRM_BASE_URL}/attendance/settings`),
  updateSettings: (data) => api.post(`${HRM_BASE_URL}/attendance/settings`, data),

  // Lock/Unlock/Process Records
  lockRecords: (data) => api.post(`${HRM_BASE_URL}/attendance/lock`, data),
  unlockRecords: (data) => api.post(`${HRM_BASE_URL}/attendance/unlock`, data),
  processRecords: (data) => api.post(`${HRM_BASE_URL}/attendance/process`, data),

  // Admin Manual Add
  addAdminAttendanceRecord: (data) => api.post(`${HRM_BASE_URL}/attendance/admin/add-record`, data),

  // Attendance Summary List
  getAttendanceSummaryList: (params = {}, config = {}) => api.get(`${HRM_BASE_URL}/attendance/summary-list`, { params, ...config }),

  // Late IN & Early Out dedicated summary
  getAttendanceLateEarlySummary: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/late-early-summary`, { params }),

  // Pre OT & Post OT dedicated summary
  getAttendancePrePostOTSummary: (params = {}) => api.get(`${HRM_BASE_URL}/attendance/pre-post-ot-summary`, { params }),

  // Lieu Leave Workflow
  getLieuLeavePendingDeclarations: () => api.get(`${HRM_BASE_URL}/attendance/lieu-leave/pending-declarations`),
  getLieuLeaveMyEntries: () => api.get(`${HRM_BASE_URL}/attendance/lieu-leave/my-entries`),
  declareLieuLeaveChoice: (data) => api.post(`${HRM_BASE_URL}/attendance/lieu-leave/declare-choice`, data),
  getLieuLeaveAllEntries: (params) => api.get(`${HRM_BASE_URL}/attendance/lieu-leave/all-entries`, { params }),
  adminApproveLieuLeave: (data) => api.post(`${HRM_BASE_URL}/attendance/lieu-leave/admin-approve`, data),
};


// ============================================================================
// LEAVE MANAGEMENT
// ============================================================================

export const leaveApi = {
  // Leave Types
  getLeaveTypes: (params = {}) => api.get(`${HRM_BASE_URL}/leave/types`, { params }),
  getLeaveTypeById: (id) => api.get(`${HRM_BASE_URL}/leave/types/${id}`),
  createLeaveType: (data) => api.post(`${HRM_BASE_URL}/leave/types`, data),
  updateLeaveType: (id, data) => api.put(`${HRM_BASE_URL}/leave/types/${id}`, data),
  deleteLeaveType: (id) => api.delete(`${HRM_BASE_URL}/leave/types/${id}`),

  // Leave Applications
  getLeaveApplications: (params = {}) => api.get(`${HRM_BASE_URL}/leave/applications`, { params }),
  getLeaveApplicationById: (id) => api.get(`${HRM_BASE_URL}/leave/applications/${id}`),
  getLeaveApplication: (id) => api.get(`${HRM_BASE_URL}/leave/applications/${id}`), // Alias for backward compatibility
  createLeaveApplication: (data) => api.post(`${HRM_BASE_URL}/leave/applications`, data),
  updateLeaveApplication: (id, data) => api.put(`${HRM_BASE_URL}/leave/applications/${id}`, data),
  deleteLeaveApplication: (id) => api.delete(`${HRM_BASE_URL}/leave/applications/${id}`),
  cancelLeaveApplication: (id, data) => api.post(`${HRM_BASE_URL}/leave/applications/${id}/cancel`, data),
  createLeaveForEmployee: (data) => api.post(`${HRM_BASE_URL}/leave/applications/create-for-employee`, data),

  // Leave Approvals
  approveLeave: (id, data) => api.post(`${HRM_BASE_URL}/leave/applications/${id}/approve`, data),
  rejectLeave: (id, data) => api.post(`${HRM_BASE_URL}/leave/applications/${id}/reject`, data),
  approveLeaveApplication: (id, data) => api.post(`${HRM_BASE_URL}/leave/applications/${id}/approve`, data), // Alias
  rejectLeaveApplication: (id, data) => api.post(`${HRM_BASE_URL}/leave/applications/${id}/reject`, data), // Alias

  // Leave Balance
  getLeaveBalance: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/leave/balance/${employeeId}`, { params }),
  getAllLeaveBalances: (params = {}) => api.get(`${HRM_BASE_URL}/leave/balances`, { params }),
  adjustLeaveBalance: (employeeId, data) => api.post(`${HRM_BASE_URL}/leave/balance/${employeeId}/adjust`, data),

  // Leave Reports
  getLeaveReport: (params = {}) => api.get(`${HRM_BASE_URL}/leave/report`, { params }),
  exportLeaveReport: (params = {}) => api.get(`${HRM_BASE_URL}/leave/export`, { params, responseType: 'blob' }),

  // Leave Calendar
  getLeaveCalendar: (params = {}) => api.get(`${HRM_BASE_URL}/leave/calendar`, { params }),
  getCalendarEvents: (params = {}) => api.get(`${HRM_BASE_URL}/leave/calendar/events`, { params }),

  // Leave Approval Configuration (2-Level Workflow: Person 01 & Person 02)
  getLeaveApprovalConfigs: (params = {}) => api.get(`${HRM_BASE_URL}/leave/approval-configs`, { params }),
  getLeaveApprovalConfigById: (id) => api.get(`${HRM_BASE_URL}/leave/approval-configs/${id}`),
  createLeaveApprovalConfig: (data) => api.post(`${HRM_BASE_URL}/leave/approval-configs`, data),
  updateLeaveApprovalConfig: (id, data) => api.put(`${HRM_BASE_URL}/leave/approval-configs/${id}`, data),
  deleteLeaveApprovalConfig: (id) => api.delete(`${HRM_BASE_URL}/leave/approval-configs/${id}`),
  getDefaultApprovalConfig: (locationId) => api.get(`${HRM_BASE_URL}/leave/approval-configs/default/${locationId}`),

  // Leave Policies
  getLeavePolicies: (params = {}) => api.get(`${HRM_BASE_URL}/leave/policies`, { params }),
  getLeavePolicyById: (id) => api.get(`${HRM_BASE_URL}/leave/policies/${id}`),
  createLeavePolicy: (data) => api.post(`${HRM_BASE_URL}/leave/policies`, data),
  updateLeavePolicy: (id, data) => api.put(`${HRM_BASE_URL}/leave/policies/${id}`, data),
  deleteLeavePolicy: (id) => api.delete(`${HRM_BASE_URL}/leave/policies/${id}`),

  // Leave Allocations
  getLeaveAllocations: (params = {}) => api.get(`${HRM_BASE_URL}/leave/allocations`, { params }),
  getLeaveAllocationById: (id) => api.get(`${HRM_BASE_URL}/leave/allocations/${id}`),
  createLeaveAllocation: (data) => api.post(`${HRM_BASE_URL}/leave/allocations`, data),
  updateLeaveAllocation: (id, data) => api.put(`${HRM_BASE_URL}/leave/allocations/${id}`, data),
  deleteLeaveAllocation: (id) => api.delete(`${HRM_BASE_URL}/leave/allocations/${id}`),

  // Leave Encashment
  getLeaveEncashments: (params = {}) => api.get(`${HRM_BASE_URL}/leave/encashments`, { params }),
  getLeaveEncashmentById: (id) => api.get(`${HRM_BASE_URL}/leave/encashments/${id}`),
  createLeaveEncashment: (data) => api.post(`${HRM_BASE_URL}/leave/encashments`, data),
  updateLeaveEncashment: (id, data) => api.put(`${HRM_BASE_URL}/leave/encashments/${id}`, data),
  approveLeaveEncashment: (id, data) => api.post(`${HRM_BASE_URL}/leave/encashments/${id}/approve`, data),
  rejectLeaveEncashment: (id, data) => api.post(`${HRM_BASE_URL}/leave/encashments/${id}/reject`, data),

  // Leave Adjustments
  getLeaveAdjustments: (params = {}) => api.get(`${HRM_BASE_URL}/leave/adjustments`, { params }),
  getLeaveAdjustmentById: (id) => api.get(`${HRM_BASE_URL}/leave/adjustments/${id}`),
  createLeaveAdjustment: (data) => api.post(`${HRM_BASE_URL}/leave/adjustments`, data),
  updateLeaveAdjustment: (id, data) => api.put(`${HRM_BASE_URL}/leave/adjustments/${id}`, data),
  deleteLeaveAdjustment: (id) => api.delete(`${HRM_BASE_URL}/leave/adjustments/${id}`),

  // Leave Cancel Requests
  getLeaveCancelRequests: (params = {}) => api.get(`${HRM_BASE_URL}/admin/cancel-requests`, { params }),
  getLeaveCancelRequestById: (id) => api.get(`${HRM_BASE_URL}/admin/cancel-requests/${id}`),
  createLeaveCancelRequest: (data) => api.post(`${HRM_BASE_URL}/admin/cancel-requests`, data),
  updateLeaveCancelRequest: (id, data) => api.put(`${HRM_BASE_URL}/admin/cancel-requests/${id}`, data),
  deleteLeaveCancelRequest: (id) => api.delete(`${HRM_BASE_URL}/admin/cancel-requests/${id}`),
  approveLeaveCancelRequest: (id, data) => api.post(`${HRM_BASE_URL}/admin/cancel-requests/${id}/approve`, data),
  rejectLeaveCancelRequest: (id, data) => api.post(`${HRM_BASE_URL}/admin/cancel-requests/${id}/reject`, data),

  // Self-Service Leave Cancel Requests
  getMyLeaveCancelRequests: (params = {}) => api.get(`${HRM_BASE_URL}/self/cancel-requests`, { params }),
  getMyLeaveCancelRequestById: (id) => api.get(`${HRM_BASE_URL}/self/cancel-requests/${id}`),
  createMyLeaveCancelRequest: (data) => api.post(`${HRM_BASE_URL}/self/cancel-requests`, data),
  updateMyLeaveCancelRequest: (id, data) => api.put(`${HRM_BASE_URL}/self/cancel-requests/${id}`, data),
  deleteMyLeaveCancelRequest: (id) => api.delete(`${HRM_BASE_URL}/self/cancel-requests/${id}`),

  // Leave Settings
  getSettings: () => api.get(`${HRM_BASE_URL}/leave/settings`),
  updateSettings: (data) => api.put(`${HRM_BASE_URL}/leave/settings`, data),
  
  // Validate Off Day & Get Monthly Off Days
  validateOffDay: (params) => api.get(`${HRM_BASE_URL}/leave/validate-off-day`, { params }),
  getOffDays: (params) => api.get(`${HRM_BASE_URL}/leave/off-days`, { params }),

  // Admin Operations
  getEmployeeBlockedDates: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/leave/admin/blocked-dates/${employeeId}`, { params }),
};

// ============================================================================
// RECRUITMENT & ATS
// ============================================================================

export const recruitmentApi = {
  // Job Requisitions
  getRequisitions: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/requisitions`, { params }),
  getRequisitionById: (id) => api.get(`${HRM_BASE_URL}/recruitment/requisitions/${id}`),
  createRequisition: (data) => api.post(`${HRM_BASE_URL}/recruitment/requisitions`, data),
  updateRequisition: (id, data) => api.put(`${HRM_BASE_URL}/recruitment/requisitions/${id}`, data),
  deleteRequisition: (id) => api.delete(`${HRM_BASE_URL}/recruitment/requisitions/${id}`),
  approveRequisition: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/requisitions/${id}/approve`, data),
  rejectRequisition: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/requisitions/${id}/reject`, data),

  // Job Postings
  getJobPostings: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/postings`, { params }),
  getJobPostingById: (id) => api.get(`${HRM_BASE_URL}/recruitment/postings/${id}`),
  createJobPosting: (data) => api.post(`${HRM_BASE_URL}/recruitment/postings`, data),
  updateJobPosting: (id, data) => api.put(`${HRM_BASE_URL}/recruitment/postings/${id}`, data),
  deleteJobPosting: (id) => api.delete(`${HRM_BASE_URL}/recruitment/postings/${id}`),
  publishJobPosting: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/postings/${id}/publish`, data),
  closeJobPosting: (id) => api.post(`${HRM_BASE_URL}/recruitment/postings/${id}/close`),

  // Candidates
  getCandidates: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/candidates`, { params }),
  getCandidateById: (id) => api.get(`${HRM_BASE_URL}/recruitment/candidates/${id}`),
  createCandidate: (data) => api.post(`${HRM_BASE_URL}/recruitment/candidates`, data),
  updateCandidate: (id, data) => api.put(`${HRM_BASE_URL}/recruitment/candidates/${id}`, data),
  deleteCandidate: (id) => api.delete(`${HRM_BASE_URL}/recruitment/candidates/${id}`),
  blacklistCandidate: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/candidates/${id}/blacklist`, data),
  addCandidateNote: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/candidates/${id}/notes`, data),

  // Applications
  getApplications: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/applications`, { params }),
  getApplicationById: (id) => api.get(`${HRM_BASE_URL}/recruitment/applications/${id}`),
  submitApplication: (data) => api.post(`${HRM_BASE_URL}/recruitment/applications`, data),
  updateApplication: (id, data) => api.put(`${HRM_BASE_URL}/recruitment/applications/${id}`, data),
  moveApplicationStage: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/applications/${id}/move-stage`, data),
  rejectApplication: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/applications/${id}/reject`, data),
  addApplicationNote: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/applications/${id}/notes`, data),

  // Interviews
  getInterviews: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/interviews`, { params }),
  getInterviewById: (id) => api.get(`${HRM_BASE_URL}/recruitment/interviews/${id}`),
  scheduleInterview: (data) => api.post(`${HRM_BASE_URL}/recruitment/interviews`, data),
  updateInterview: (id, data) => api.put(`${HRM_BASE_URL}/recruitment/interviews/${id}`, data),
  cancelInterview: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/interviews/${id}/cancel`, data),
  submitInterviewFeedback: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/interviews/${id}/feedback`, data),
  markInterviewCompleted: (id) => api.post(`${HRM_BASE_URL}/recruitment/interviews/${id}/complete`),

  // Offers
  getOffers: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/offers`, { params }),
  getOfferById: (id) => api.get(`${HRM_BASE_URL}/recruitment/offers/${id}`),
  generateOffer: (data) => api.post(`${HRM_BASE_URL}/recruitment/offers`, data),
  updateOffer: (id, data) => api.put(`${HRM_BASE_URL}/recruitment/offers/${id}`, data),
  approveOffer: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/offers/${id}/approve`, data),
  sendOffer: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/offers/${id}/send`, data),
  acceptOffer: (id) => api.post(`${HRM_BASE_URL}/recruitment/offers/${id}/accept`),
  declineOffer: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/offers/${id}/decline`, data),
  withdrawOffer: (id, data) => api.post(`${HRM_BASE_URL}/recruitment/offers/${id}/withdraw`, data),

  // Analytics
  getRecruitmentAnalytics: (params = {}) => api.get(`${HRM_BASE_URL}/recruitment/analytics`, { params }),
  getRecruitmentDashboard: () => api.get(`${HRM_BASE_URL}/recruitment/dashboard`),
};

// ============================================================================
// PERFORMANCE MANAGEMENT
// ============================================================================

export const performanceApi = {
  // Dashboard
  getDashboard: () => api.get(`${HRM_BASE_URL}/performance/dashboard`),

  // Performance Reviews
  getReviews: (params = {}) => api.get(`${HRM_BASE_URL}/performance/reviews`, { params }),
  getReviewById: (id) => api.get(`${HRM_BASE_URL}/performance/reviews/${id}`),
  createReview: (data) => api.post(`${HRM_BASE_URL}/performance/reviews`, data),
  updateReview: (id, data) => api.put(`${HRM_BASE_URL}/performance/reviews/${id}`, data),
  deleteReview: (id) => api.delete(`${HRM_BASE_URL}/performance/reviews/${id}`),
  submitReview: (id) => api.post(`${HRM_BASE_URL}/performance/reviews/${id}/submit`),
  approveReview: (id) => api.post(`${HRM_BASE_URL}/performance/reviews/${id}/approve`),
  completeReview: (id) => api.post(`${HRM_BASE_URL}/performance/reviews/${id}/complete`),

  // Self & Manager Assessments
  getSelfAssessment: (reviewId) => api.get(`${HRM_BASE_URL}/performance/reviews/${reviewId}/self-assessment`),
  submitSelfAssessment: (reviewId, data) => api.post(`${HRM_BASE_URL}/performance/reviews/${reviewId}/self-assessment`, data),
  getManagerAssessment: (reviewId) => api.get(`${HRM_BASE_URL}/performance/reviews/${reviewId}/manager-assessment`),
  submitManagerAssessment: (reviewId, data) => api.post(`${HRM_BASE_URL}/performance/reviews/${reviewId}/manager-assessment`, data),

  // Goals & Objectives
  getGoals: (params = {}) => api.get(`${HRM_BASE_URL}/performance/goals`, { params }),
  getGoalById: (id) => api.get(`${HRM_BASE_URL}/performance/goals/${id}`),
  createGoal: (data) => api.post(`${HRM_BASE_URL}/performance/goals`, data),
  updateGoal: (id, data) => api.put(`${HRM_BASE_URL}/performance/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`${HRM_BASE_URL}/performance/goals/${id}`),
  updateGoalProgress: (id, data) => api.post(`${HRM_BASE_URL}/performance/goals/${id}/progress`, data),

  // KPIs
  getKPIs: (params = {}) => api.get(`${HRM_BASE_URL}/performance/kpis`, { params }),
  getKPIById: (id) => api.get(`${HRM_BASE_URL}/performance/kpis/${id}`),
  createKPI: (data) => api.post(`${HRM_BASE_URL}/performance/kpis`, data),
  updateKPI: (id, data) => api.put(`${HRM_BASE_URL}/performance/kpis/${id}`, data),
  deleteKPI: (id) => api.delete(`${HRM_BASE_URL}/performance/kpis/${id}`),

  // Feedback (360-degree)
  getFeedback: (params = {}) => api.get(`${HRM_BASE_URL}/performance/feedback`, { params }),
  getFeedbackById: (id) => api.get(`${HRM_BASE_URL}/performance/feedback/${id}`),
  createFeedback: (data) => api.post(`${HRM_BASE_URL}/performance/feedback`, data),
  updateFeedback: (id, data) => api.put(`${HRM_BASE_URL}/performance/feedback/${id}`, data),
  deleteFeedback: (id) => api.delete(`${HRM_BASE_URL}/performance/feedback/${id}`),

  // Review Templates
  getTemplates: (params = {}) => api.get(`${HRM_BASE_URL}/performance/templates`, { params }),
  getTemplateById: (id) => api.get(`${HRM_BASE_URL}/performance/templates/${id}`),
  createTemplate: (data) => api.post(`${HRM_BASE_URL}/performance/templates`, data),
  updateTemplate: (id, data) => api.put(`${HRM_BASE_URL}/performance/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`${HRM_BASE_URL}/performance/templates/${id}`),

  // Competencies
  getCompetencies: (params = {}) => api.get(`${HRM_BASE_URL}/performance/competencies`, { params }),
  createCompetency: (data) => api.post(`${HRM_BASE_URL}/performance/competencies`, data),
  updateCompetency: (id, data) => api.put(`${HRM_BASE_URL}/performance/competencies/${id}`, data),
  deleteCompetency: (id) => api.delete(`${HRM_BASE_URL}/performance/competencies/${id}`),

  // Reports
  getPerformanceSummary: (params = {}) => api.get(`${HRM_BASE_URL}/performance/reports/summary`, { params }),
  getRatingsReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance/reports/ratings`, { params }),
  getGoalsReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance/reports/goals`, { params }),
  exportReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance/reports/export`, { params, responseType: 'blob' }),
};

// ============================================================================
// ============================================================================
// PAYROLL MANAGEMENT
// ============================================================================

export const payrollApi = {
  // Dashboard
  getDashboard: () => api.get(`${HRM_BASE_URL}/payroll/dashboard`),

  // Payroll Cycles
  getPayrollCycles: (params = {}) => api.get(`${HRM_BASE_URL}/payroll-cycles`, { params }),
  getPayrollPeriods: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/periods`, { params }),
  getPayrollCycleById: (id) => api.get(`${HRM_BASE_URL}/payroll/cycles/${id}`),
  createPayrollCycle: (data) => api.post(`${HRM_BASE_URL}/payroll/cycles`, data),
  updatePayrollCycle: (id, data) => api.put(`${HRM_BASE_URL}/payroll/cycles/${id}`, data),
  processPayroll: (id) => api.post(`${HRM_BASE_URL}/payroll/cycles/${id}/process`),
  approvePayroll: (id, data) => api.post(`${HRM_BASE_URL}/payroll/cycles/${id}/approve`, data),
  finalizePayroll: (id) => api.post(`${HRM_BASE_URL}/payroll/cycles/${id}/finalize`),

  // Payslips
  getPayslips: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/payslips`, { params }),
  getPayslipById: (id) => api.get(`${HRM_BASE_URL}/payroll/payslips/${id}`),
  getEmployeePayslips: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/payroll/payslips/employee/${employeeId}`, { params }),
  sendPayslip: (id) => api.post(`${HRM_BASE_URL}/payroll/payslips/${id}/send`),
  downloadPayslip: (id) => api.get(`${HRM_BASE_URL}/payroll/payslips/${id}/download`, { responseType: 'blob' }),

  // Payroll Groups Configuration
  payrollGroups: {
    getAll: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/groups`, { params }),
    getById: (id) => api.get(`${HRM_BASE_URL}/payroll/groups/${id}`),
    create: (data) => api.post(`${HRM_BASE_URL}/payroll/groups`, data),
    update: (id, data) => api.put(`${HRM_BASE_URL}/payroll/groups/${id}`, data),
    delete: (id) => api.delete(`${HRM_BASE_URL}/payroll/groups/${id}`),
    getEmployees: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/groups/employees`, { params }),
    assignEmployees: (groupId, data) => api.post(`${HRM_BASE_URL}/payroll/groups/${groupId}/assign`, data),
    unassignEmployees: (groupId, data) => api.post(`${HRM_BASE_URL}/payroll/groups/${groupId}/unassign`, data),
  },

  // Payroll Component Configurations (Template definitions)
  fixedAllowanceConfig: {
    getAll: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/fixed-allowance-configs`, { params }),
    getById: (id) => api.get(`${HRM_BASE_URL}/payroll/fixed-allowance-configs/${id}`),
    create: (data) => api.post(`${HRM_BASE_URL}/payroll/fixed-allowance-configs`, data),
    update: (id, data) => api.put(`${HRM_BASE_URL}/payroll/fixed-allowance-configs/${id}`, data),
    delete: (id) => api.delete(`${HRM_BASE_URL}/payroll/fixed-allowance-configs/${id}`),
  },

  variablePaymentConfig: {
    getAll: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/variable-payment-configs`, { params }),
    getById: (id) => api.get(`${HRM_BASE_URL}/payroll/variable-payment-configs/${id}`),
    create: (data) => api.post(`${HRM_BASE_URL}/payroll/variable-payment-configs`, data),
    update: (id, data) => api.put(`${HRM_BASE_URL}/payroll/variable-payment-configs/${id}`, data),
    delete: (id) => api.delete(`${HRM_BASE_URL}/payroll/variable-payment-configs/${id}`),
  },

  variableAllowanceConfig: {
    getAll: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/variable-allowance-configs`, { params }),
    getById: (id) => api.get(`${HRM_BASE_URL}/payroll/variable-allowance-configs/${id}`),
    create: (data) => api.post(`${HRM_BASE_URL}/payroll/variable-allowance-configs`, data),
    update: (id, data) => api.put(`${HRM_BASE_URL}/payroll/variable-allowance-configs/${id}`, data),
    delete: (id) => api.delete(`${HRM_BASE_URL}/payroll/variable-allowance-configs/${id}`),
  },

  otherDeductionConfig: {
    getAll: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/other-deduction-configs`, { params }),
    getById: (id) => api.get(`${HRM_BASE_URL}/payroll/other-deduction-configs/${id}`),
    create: (data) => api.post(`${HRM_BASE_URL}/payroll/other-deduction-configs`, data),
    update: (id, data) => api.put(`${HRM_BASE_URL}/payroll/other-deduction-configs/${id}`, data),
    delete: (id) => api.delete(`${HRM_BASE_URL}/payroll/other-deduction-configs/${id}`),
  },

  // Salary Components (Legacy/General)
  getSalaryComponents: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/salary-components`, { params }),
  getSalaryComponentById: (id) => api.get(`${HRM_BASE_URL}/payroll/salary-components/${id}`),
  createSalaryComponent: (data) => api.post(`${HRM_BASE_URL}/payroll/salary-components`, data),
  updateSalaryComponent: (id, data) => api.put(`${HRM_BASE_URL}/payroll/salary-components/${id}`, data),
  deleteSalaryComponent: (id) => api.delete(`${HRM_BASE_URL}/payroll/salary-components/${id}`),

  // Component Categories
  getComponentCategories: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/component-categories`, { params }),
  getComponentCategoryById: (id) => api.get(`${HRM_BASE_URL}/payroll/component-categories/${id}`),
  createComponentCategory: (data) => api.post(`${HRM_BASE_URL}/payroll/component-categories`, data),
  updateComponentCategory: (id, data) => api.put(`${HRM_BASE_URL}/payroll/component-categories/${id}`, data),
  deleteComponentCategory: (id) => api.delete(`${HRM_BASE_URL}/payroll/component-categories/${id}`),

  // Employee Salary
  getEmployeeSalaries: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/employee-salary`, { params }),
  getEmployeeSalary: (employeeId) => api.get(`${HRM_BASE_URL}/payroll/employee-salary/employee/${employeeId}`),
  createEmployeeSalary: (data) => api.post(`${HRM_BASE_URL}/payroll/employee-salary`, data),
  bulkAssignSalaryCategory: (data) => api.post(`${HRM_BASE_URL}/payroll/employee-salary/bulk-assign`, data),
  updateEmployeeSalary: (id, data) => api.put(`${HRM_BASE_URL}/payroll/employee-salary/${id}`, data),

  // Tax & Deductions
  getTaxSlabs: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/tax-slabs`, { params }),
  createTaxSlab: (data) => api.post(`${HRM_BASE_URL}/payroll/tax-slabs`, data),
  updateTaxSlab: (id, data) => api.put(`${HRM_BASE_URL}/payroll/tax-slabs/${id}`, data),
  deleteTaxSlab: (id) => api.delete(`${HRM_BASE_URL}/payroll/tax-slabs/${id}`),

  // Reports
  getPayrollSummary: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/reports/summary`, { params }),
  getTaxReport: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/reports/tax-report`, { params }),
  exportPayrollReport: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/reports/export`, { params, responseType: 'blob' }),

  // Loans & Advances
  loans: {
    getAll: (params = {}) => api.get(`${HRM_BASE_URL}/payroll/loans`, { params }),
    getById: (id) => api.get(`${HRM_BASE_URL}/payroll/loans/${id}`),
    create: (data) => api.post(`${HRM_BASE_URL}/payroll/loans`, data),
    update: (id, data) => api.put(`${HRM_BASE_URL}/payroll/loans/${id}`, data),
    delete: (id) => api.delete(`${HRM_BASE_URL}/payroll/loans/${id}`),
    approve: (id) => api.post(`${HRM_BASE_URL}/payroll/loans/${id}/approve`),
    close: (id, data) => api.post(`${HRM_BASE_URL}/payroll/loans/${id}/close`, data),
    getStats: () => api.get(`${HRM_BASE_URL}/payroll/loans/stats`),
    recordRepayment: (id, data) => api.post(`${HRM_BASE_URL}/payroll/loans/${id}/repayments`, data),
    getRepayments: (id) => api.get(`${HRM_BASE_URL}/payroll/loans/${id}/repayments`),
    revertRepayment: (repaymentId) => api.post(`${HRM_BASE_URL}/payroll/loans/repayments/${repaymentId}/revert`),
  },
  salarySetup: {
    getDropdownActivePeriods: () => api.get(`${HRM_BASE_URL}/payroll/salary-setup/dropdown/active-periods`),
    getEmployeesForSetup: (periodId) => api.get(`${HRM_BASE_URL}/payroll/salary-setup/employees/${periodId}`),
    initialize: (data) => api.post(`${HRM_BASE_URL}/payroll/salary-setup/initialize`, data),
    getByPeriod: (periodId) => api.get(`${HRM_BASE_URL}/payroll/salary-setup/${periodId}`),
    updateDetail: (detailId, data) => api.put(`${HRM_BASE_URL}/payroll/salary-setup/detail/${detailId}`, data),
    upsertDetail: (data) => api.post(`${HRM_BASE_URL}/payroll/salary-setup/upsert-detail`, data),
    bulkUpsertDetail: (data) => api.post(`${HRM_BASE_URL}/payroll/salary-setup/bulk-upsert-detail`, data),
    getEmployeeMatrix: (periodId, employeeId) => api.get(`${HRM_BASE_URL}/payroll/salary-setup/employee-matrix/${periodId}/${employeeId}`),
    downloadImportTemplate: () => api.get(`${HRM_BASE_URL}/payroll/salary-setup/import/template`, { responseType: 'blob' }),
    previewSalarySetupExcel: (formData) => api.post(`${HRM_BASE_URL}/payroll/salary-setup/import/preview`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadSalarySetupExcel: (formData) => api.post(`${HRM_BASE_URL}/payroll/salary-setup/import/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    finalize: (setupId) => api.post(`${HRM_BASE_URL}/payroll/salary-setup/finalize/${setupId}`),
    // Bulk sync node implementation
  },

  // Employee Self-Service (ESS) Payroll
  ess: {
    getOverview: () => api.get(`${HRM_BASE_URL}/ess/payroll/overview`),
    getPayslips: () => api.get(`${HRM_BASE_URL}/ess/payroll/payslips`),
    getPayslipById: (id) => api.get(`${HRM_BASE_URL}/ess/payroll/payslips/${id}`),
  },
};

// ============================================================================
// HRM DASHBOARD & REPORTS (Refreshed)
// ============================================================================
export const hrmDashboardApi = {
  getDashboardStats: () => api.get(`${HRM_BASE_URL}/dashboard/stats`),
  getPendingCounts: () => api.get(`${HRM_BASE_URL}/dashboard/stats/pending`),
  getStats: () => api.get(`${HRM_BASE_URL}/dashboard/stats`), // Alias
  getEmployeeStats: () => api.get(`${HRM_BASE_URL}/dashboard/employees`),
  getAttendanceStats: (params = {}) => api.get(`${HRM_BASE_URL}/dashboard/attendance`, { params }),
  getLeaveStats: (params = {}) => api.get(`${HRM_BASE_URL}/dashboard/leave`, { params }),
  getRecruitmentStats: () => api.get(`${HRM_BASE_URL}/dashboard/recruitment`),
  getPerformanceStats: () => api.get(`${HRM_BASE_URL}/dashboard/performance`),
  getRecentActivities: (params = {}) => api.get(`${HRM_BASE_URL}/dashboard/activities`, { params }),
};

// ============================================================================
// EMPLOYEE LIFE CYCLE
// ============================================================================

export const employeeLifeCycleApi = {
  // Dashboard
  getDashboardStats: () => api.get(`${HRM_BASE_URL}/employee-lifecycle/dashboard-stats`),

  // Onboarding
  getOnboarding: (params = {}) => api.get(`${HRM_BASE_URL}/employee-lifecycle/onboarding`, { params }),
  getOnboardingById: (id) => api.get(`${HRM_BASE_URL}/employee-lifecycle/onboarding/${id}`),
  createOnboarding: (data) => api.post(`${HRM_BASE_URL}/employee-lifecycle/onboarding`, data),
  updateOnboarding: (id, data) => api.patch(`${HRM_BASE_URL}/employee-lifecycle/onboarding/${id}`, data),
  deleteOnboarding: (id) => api.delete(`${HRM_BASE_URL}/employee-lifecycle/onboarding/${id}`),

  // Confirmation
  getConfirmations: (params = {}) => api.get(`${HRM_BASE_URL}/employee-lifecycle/confirmation`, { params }),
  getConfirmationById: (id) => api.get(`${HRM_BASE_URL}/employee-lifecycle/confirmation/${id}`),
  createConfirmation: (data) => api.post(`${HRM_BASE_URL}/employee-lifecycle/confirmation`, data),
  updateConfirmation: (id, data) => api.patch(`${HRM_BASE_URL}/employee-lifecycle/confirmation/${id}`, data),
  deleteConfirmation: (id) => api.delete(`${HRM_BASE_URL}/employee-lifecycle/confirmation/${id}`),

  // Job Changes
  getJobChanges: (params = {}) => api.get(`${HRM_BASE_URL}/employee-lifecycle/job-change`, { params }),
  getJobChangeById: (id) => api.get(`${HRM_BASE_URL}/employee-lifecycle/job-change/${id}`),
  createJobChange: (data) => api.post(`${HRM_BASE_URL}/employee-lifecycle/job-change`, data),
  updateJobChange: (id, data) => api.patch(`${HRM_BASE_URL}/employee-lifecycle/job-change/${id}`, data),
  deleteJobChange: (id) => api.delete(`${HRM_BASE_URL}/employee-lifecycle/job-change/${id}`),

  // Separations
  getSeparations: (params = {}) => api.get(`${HRM_BASE_URL}/employee-lifecycle/separation`, { params }),
  getSeparationById: (id) => api.get(`${HRM_BASE_URL}/employee-lifecycle/separation/${id}`),
  createSeparation: (data) => api.post(`${HRM_BASE_URL}/employee-lifecycle/separation`, data),
  updateSeparation: (id, data) => api.patch(`${HRM_BASE_URL}/employee-lifecycle/separation/${id}`, data),
  deleteSeparation: (id) => api.delete(`${HRM_BASE_URL}/employee-lifecycle/separation/${id}`),
  revertSeparation: (id) => api.post(`${HRM_BASE_URL}/employee-lifecycle/separation/${id}/revert`),

  // Employee History
  getEmployeeHistory: (employeeId) => api.get(`${HRM_BASE_URL}/employee-lifecycle/history/${employeeId}`),
  getAllHistory: (params = {}) => api.get(`${HRM_BASE_URL}/employee-lifecycle/history`, { params }),
  exportHistory: (params = {}) => api.get(`${HRM_BASE_URL}/employee-lifecycle/export-history`, { params, responseType: 'blob' }),

  // Bulk Import
  getImportTemplateData: () => api.get(`${HRM_BASE_URL}/employee-lifecycle/import/template-data`),
  previewImport: (data) => api.post(`${HRM_BASE_URL}/employee-lifecycle/import/preview`, { data }),
  confirmImport: (data) => api.post(`${HRM_BASE_URL}/employee-lifecycle/import/confirm`, { data }),
};

// ============================================================================
// WORK CALENDAR
// ============================================================================

export const workCalendarApi = {
  getCalendarData: (params = {}) => api.get(`${HRM_BASE_URL}/work-calendar`, { params }),
  saveDiaryNote: (data) => api.post(`${HRM_BASE_URL}/work-calendar/diary`, data),
  deleteDiaryNote: (id) => api.delete(`${HRM_BASE_URL}/work-calendar/diary/${id}`),
};

export const fileApi = {
  uploadFile: (formData) => api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadFile: (fileId) => api.get(`/files/download/${fileId}`, { responseType: 'blob' }),
  getFileMetadata: (fileId) => api.get(`/files/${fileId}`)
};


// ============================================================================
// ============================================================================
// EMPLOYEE SELF-SERVICE (ESS)
// ============================================================================

export const essApi = {
  // Profile
  getMyProfile: () => api.get(`${HRM_BASE_URL}/ess/profile`),
  updateMyProfile: (data) => api.patch(`${HRM_BASE_URL}/ess/profile`, data),
  getViewProfileSelf: () => api.get(`${HRM_BASE_URL}/ess/view-profile-self`),
  getDashboardStats: () => api.get(`${HRM_BASE_URL}/ess/dashboard-stats`),
  getDashboardData: () => api.get(`${HRM_BASE_URL}/ess/dashboard-data`),
  updateProfilePhoto: (formData) => api.put(`${HRM_BASE_URL}/ess/profile/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Expense Claims
  createExpense: (formData) => api.post(`${HRM_BASE_URL}/ess/expenses`, formData),
  getMyExpenses: (params = {}) => api.get(`${HRM_BASE_URL}/ess/expenses`, { params }),
  getExpenseById: (id) => api.get(`${HRM_BASE_URL}/ess/expenses/${id}`),
  updateExpense: (id, formData) => api.patch(`${HRM_BASE_URL}/ess/expenses/${id}`, formData),
  deleteExpense: (id) => api.delete(`${HRM_BASE_URL}/ess/expenses/${id}`),

  // HR Tickets
  createTicket: (formData) => api.post(`${HRM_BASE_URL}/ess/tickets`, formData),
  getMyTickets: (params = {}) => api.get(`${HRM_BASE_URL}/ess/tickets`, { params }),
  getTicketById: (id) => api.get(`${HRM_BASE_URL}/ess/tickets/${id}`),
  addTicketReply: (id, message) => api.post(`${HRM_BASE_URL}/ess/tickets/${id}/reply`, { message }),
  closeTicket: (id, reason) => api.put(`${HRM_BASE_URL}/ess/tickets/${id}/close`, { reason }),

  // Resignation
  createResignation: (data) => api.post(`${HRM_BASE_URL}/ess/resignation`, data),
  getMyResignation: () => api.get(`${HRM_BASE_URL}/ess/resignation`),
  updateResignation: (id, data) => api.put(`${HRM_BASE_URL}/ess/resignation/${id}`, data),
  cancelResignation: (id) => api.post(`${HRM_BASE_URL}/ess/resignation/${id}/cancel`),
};

// ============================================================================
// ADMIN TICKET MANAGEMENT
// ============================================================================

export const adminTicketApi = {
  getAllTickets: (params = {}) => api.get(`${HRM_BASE_URL}/admin/tickets`, { params }),
  getTicketById: (id) => api.get(`${HRM_BASE_URL}/admin/tickets/${id}`),
  updateTicketStatus: (id, data) => api.patch(`${HRM_BASE_URL}/admin/tickets/${id}/status`, data),
  addTicketReply: (id, message, isInternal) => api.post(`${HRM_BASE_URL}/admin/tickets/${id}/reply`, { message, isInternal }),
};

export const reportsApi = {
  // Employee Reports
  getEmployeeReport: (endpoint, params = {}) => api.post(`${HRM_BASE_URL}/reports${endpoint}`, params),
  exportEmployeeReport: (endpoint, params = {}) => api.post(`${HRM_BASE_URL}/reports${endpoint}`, params, { responseType: 'blob' }),

  // Attendance Reports
  getAttendanceReport: (endpoint, params = {}) => api.post(`${HRM_BASE_URL}/reports${endpoint}`, params),
  exportAttendanceReport: (endpoint, params = {}) => api.post(`${HRM_BASE_URL}/reports${endpoint}`, params, { responseType: 'blob' }),

  // Leave Reports
  getLeaveReport: (endpoint, params = {}) => api.post(`${HRM_BASE_URL}/reports${endpoint}`, params),
  exportLeaveReport: (endpoint, params = {}) => api.post(`${HRM_BASE_URL}/reports${endpoint}`, params, { responseType: 'blob' }),

  // Audit Logs Reports
  getAuditLogs: (params = {}) => api.get(`${HRM_BASE_URL}/reports/audit-logs`, { params }),
  exportAuditLogs: (params = {}) => api.post(`${HRM_BASE_URL}/reports/audit-logs/export`, params, { responseType: 'blob' }),
};

// ============================================================================
// KPI SETUP
// ============================================================================
export const kpiSetupApi = {
  // Categories
  getCategories: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-setup/categories`, { params }),
  getCategoriesDropdown: () => api.get(`${HRM_BASE_URL}/kpi-setup/categories/dropdown`),
  getCategoryById: (id) => api.get(`${HRM_BASE_URL}/kpi-setup/categories/${id}`),
  createCategory: (data) => api.post(`${HRM_BASE_URL}/kpi-setup/categories`, data),
  updateCategory: (id, data) => api.put(`${HRM_BASE_URL}/kpi-setup/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`${HRM_BASE_URL}/kpi-setup/categories/${id}`),
  // Definitions
  getDefinitions: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-setup/definitions`, { params }),
  getDefinitionsDropdown: () => api.get(`${HRM_BASE_URL}/kpi-setup/definitions/dropdown`),
  getDefinitionById: (id) => api.get(`${HRM_BASE_URL}/kpi-setup/definitions/${id}`),
  createDefinition: (data) => api.post(`${HRM_BASE_URL}/kpi-setup/definitions`, data),
  updateDefinition: (id, data) => api.put(`${HRM_BASE_URL}/kpi-setup/definitions/${id}`, data),
  deleteDefinition: (id) => api.delete(`${HRM_BASE_URL}/kpi-setup/definitions/${id}`),
  // Weights
  getWeights: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-setup/weights`, { params }),
  getWeightById: (id) => api.get(`${HRM_BASE_URL}/kpi-setup/weights/${id}`),
  createWeight: (data) => api.post(`${HRM_BASE_URL}/kpi-setup/weights`, data),
  updateWeight: (id, data) => api.put(`${HRM_BASE_URL}/kpi-setup/weights/${id}`, data),
  deleteWeight: (id) => api.delete(`${HRM_BASE_URL}/kpi-setup/weights/${id}`),
  // Rating Scales
  getRatingScales: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-setup/rating-scales`, { params }),
  getRatingScaleById: (id) => api.get(`${HRM_BASE_URL}/kpi-setup/rating-scales/${id}`),
  createRatingScale: (data) => api.post(`${HRM_BASE_URL}/kpi-setup/rating-scales`, data),
  updateRatingScale: (id, data) => api.put(`${HRM_BASE_URL}/kpi-setup/rating-scales/${id}`, data),
  deleteRatingScale: (id) => api.delete(`${HRM_BASE_URL}/kpi-setup/rating-scales/${id}`),
};

// COMMON KPIS
// ============================================================================
export const commonKpiApi = {
  getAll: () => api.get(`${HRM_BASE_URL}/common-kpis`),
  create: (data) => api.post(`${HRM_BASE_URL}/common-kpis`, data),
  update: (id, data) => api.put(`${HRM_BASE_URL}/common-kpis/${id}`, data),
  delete: (id) => api.delete(`${HRM_BASE_URL}/common-kpis/${id}`),
  getAssignments: () => api.get(`${HRM_BASE_URL}/common-kpis/assignments`),
  toggleAssignment: (employeeId, status) => api.put(`${HRM_BASE_URL}/common-kpis/assignments/${employeeId}`, { status }),
};

// ============================================================================
// KPI ASSIGNMENT
// ============================================================================
export const kpiAssignmentApi = {
  getDepartmentAssignments: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/department`, { params }),
  createDepartmentAssignment: (data) => api.post(`${HRM_BASE_URL}/kpi-assignment/department`, data),
  getEmployeeAssignments: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/employee`, { params }),
  createEmployeeAssignment: (data) => api.post(`${HRM_BASE_URL}/kpi-assignment/employee`, data),
  getAssignmentsByEmployee: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/employee/by-employee/${employeeId}`, { params }),
  getAssignmentById: (id) => api.get(`${HRM_BASE_URL}/kpi-assignment/${id}`),
  updateAssignment: (id, data) => api.put(`${HRM_BASE_URL}/kpi-assignment/${id}`, data),
  deleteAssignment: (id) => api.delete(`${HRM_BASE_URL}/kpi-assignment/${id}`),
  getTargets: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/targets`, { params }),
  getTargetsByEmployee: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/targets/by-employee/${employeeId}`, { params }),
  getTargetById: (id) => api.get(`${HRM_BASE_URL}/kpi-assignment/targets/${id}`),
  createTarget: (data) => api.post(`${HRM_BASE_URL}/kpi-assignment/targets`, data),
  updateTarget: (id, data) => api.put(`${HRM_BASE_URL}/kpi-assignment/targets/${id}`, data),
  deleteTarget: (id) => api.delete(`${HRM_BASE_URL}/kpi-assignment/targets/${id}`),
  getKPIOverview: (params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/overview`, { params }),
  updateEmployeeSupervisor: (data) => api.put(`${HRM_BASE_URL}/kpi-assignment/update-supervisor`, data),
  getEmployeeKPIs: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/kpi-assignment/employee-kpis/${employeeId}`, { params }),
  saveEmployeeKPIs: (data) => api.put(`${HRM_BASE_URL}/kpi-assignment/employee-kpis`, data),
};

// ============================================================================
// PERFORMANCE TRACKING
// ============================================================================
export const performanceTrackingApi = {
  getDashboard: (params = {}) => api.get(`${HRM_BASE_URL}/performance-tracking/dashboard`, { params }),
  getProgress: (params = {}) => api.get(`${HRM_BASE_URL}/performance-tracking/progress`, { params }),
  getProgressById: (id) => api.get(`${HRM_BASE_URL}/performance-tracking/progress/${id}`),
  getProgressByEmployee: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/performance-tracking/progress/by-employee/${employeeId}`, { params }),
  createProgress: (data) => api.post(`${HRM_BASE_URL}/performance-tracking/progress`, data),
  updateProgress: (id, data) => api.put(`${HRM_BASE_URL}/performance-tracking/progress/${id}`, data),
  deleteProgress: (id) => api.delete(`${HRM_BASE_URL}/performance-tracking/progress/${id}`),
  getManagerUpdates: (params = {}) => api.get(`${HRM_BASE_URL}/performance-tracking/manager-updates`, { params }),
  getManagerUpdateById: (id) => api.get(`${HRM_BASE_URL}/performance-tracking/manager-updates/${id}`),
  getManagerUpdatesByEmployee: (employeeId) => api.get(`${HRM_BASE_URL}/performance-tracking/manager-updates/by-employee/${employeeId}`),
  createManagerUpdate: (data) => api.post(`${HRM_BASE_URL}/performance-tracking/manager-updates`, data),
  updateManagerUpdate: (id, data) => api.put(`${HRM_BASE_URL}/performance-tracking/manager-updates/${id}`, data),
  deleteManagerUpdate: (id) => api.delete(`${HRM_BASE_URL}/performance-tracking/manager-updates/${id}`),
};

// ============================================================================
// KPI TARGET PERIODS
// ============================================================================
export const kpiTargetPeriodApi = {
  getAll: (params = {}) => api.get(`${HRM_BASE_URL}/performance-evaluation/target-periods`, { params }),
  getById: (id) => api.get(`${HRM_BASE_URL}/performance-evaluation/target-periods/${id}`),
  create: (data) => api.post(`${HRM_BASE_URL}/performance-evaluation/target-periods`, data),
  update: (id, data) => api.put(`${HRM_BASE_URL}/performance-evaluation/target-periods/${id}`, data),
  delete: (id) => api.delete(`${HRM_BASE_URL}/performance-evaluation/target-periods/${id}`),
  generateEvaluationPeriods: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/target-periods/${id}/generate-evaluation-periods`),
  start: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/target-periods/${id}/start`),
  complete: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/target-periods/${id}/complete`),
};

// ============================================================================
// PERFORMANCE EVALUATION
// ============================================================================
export const performanceEvaluationApi = {
  getReviewPeriods: (params = {}) => api.get(`${HRM_BASE_URL}/performance-evaluation/review-periods`, { params }),
  getReviewPeriodById: (id) => api.get(`${HRM_BASE_URL}/performance-evaluation/review-periods/${id}`),
  createReviewPeriod: (data) => api.post(`${HRM_BASE_URL}/performance-evaluation/review-periods`, data),
  updateReviewPeriod: (id, data) => api.put(`${HRM_BASE_URL}/performance-evaluation/review-periods/${id}`, data),
  deleteReviewPeriod: (id) => api.delete(`${HRM_BASE_URL}/performance-evaluation/review-periods/${id}`),
  activateReviewPeriod: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/review-periods/${id}/activate`),
  closeReviewPeriod: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/review-periods/${id}/close`),
  getReviewPeriodsDropdown: () => api.get(`${HRM_BASE_URL}/performance-evaluation/review-periods/dropdown`),

  getEvaluations: (params = {}) => api.get(`${HRM_BASE_URL}/performance-evaluation/evaluations`, { params }),
  getTeamEvaluations: (params = {}) => api.get(`${HRM_BASE_URL}/performance-evaluation/evaluations/team`, { params }),
  getEvaluationById: (id) => api.get(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}`),
  createEvaluation: (data) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations`, data),
  updateEvaluation: (id, data) => api.put(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}`, data),
  deleteEvaluation: (id) => api.delete(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}`),
  // Self-evaluation: save draft (supports file upload via FormData)
  saveSelfEvaluationDraft: (id, formData) => api.post(
    `${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/self-evaluation/draft`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  // Self-evaluation: final submit (supports file upload via FormData)
  submitSelfEvaluation: (id, formData) => api.post(
    `${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/self-evaluation`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  submitManagerEvaluation: (id, data) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/manager-evaluation`, data),
  extendDeadline: (id, data) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/extend-deadline`, data),
  forceOpen: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/force-open`),
  calculateScore: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/calculate-score`),
  acknowledgeEvaluation: (id) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/acknowledge`),
  sendEvalReminder: (id, data) => api.post(`${HRM_BASE_URL}/performance-evaluation/evaluations/${id}/send-reminder`, data),
};

// ============================================================================
// PERFORMANCE MANAGEMENT REPORTS
// ============================================================================
export const performanceMgmtReportsApi = {
  getEmployeeReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/employee`, { params }),
  getDepartmentReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/department`, { params }),
  getKPIAchievementReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/kpi-achievement`, { params }),
  getTopPerformers: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/top-performers`, { params }),
  getLowPerformers: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/low-performers`, { params }),
  exportEmployeeReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/employee/export`, { params, responseType: 'blob' }),
  exportDepartmentReport: (params = {}) => api.get(`${HRM_BASE_URL}/performance-mgmt-reports/department/export`, { params, responseType: 'blob' }),
};

// ============================================================================
// REWARDS & IMPROVEMENT
// ============================================================================
export const rewardsImprovementApi = {
  getRecommendations: (params = {}) => api.get(`${HRM_BASE_URL}/rewards-improvement/recommendations`, { params }),
  getRecommendationById: (id) => api.get(`${HRM_BASE_URL}/rewards-improvement/recommendations/${id}`),
  createRecommendation: (data) => api.post(`${HRM_BASE_URL}/rewards-improvement/recommendations`, data),
  updateRecommendation: (id, data) => api.put(`${HRM_BASE_URL}/rewards-improvement/recommendations/${id}`, data),
  deleteRecommendation: (id) => api.delete(`${HRM_BASE_URL}/rewards-improvement/recommendations/${id}`),
  approveRecommendation: (id, data) => api.post(`${HRM_BASE_URL}/rewards-improvement/recommendations/${id}/approve`, data),
  rejectRecommendation: (id, data) => api.post(`${HRM_BASE_URL}/rewards-improvement/recommendations/${id}/reject`, data),
  getRecommendationsByEmployee: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/rewards-improvement/recommendations/by-employee/${employeeId}`, { params }),

  getPIPs: (params = {}) => api.get(`${HRM_BASE_URL}/rewards-improvement/pip`, { params }),
  getPIPById: (id) => api.get(`${HRM_BASE_URL}/rewards-improvement/pip/${id}`),
  createPIP: (data) => api.post(`${HRM_BASE_URL}/rewards-improvement/pip`, data),
  updatePIP: (id, data) => api.put(`${HRM_BASE_URL}/rewards-improvement/pip/${id}`, data),
  deletePIP: (id) => api.delete(`${HRM_BASE_URL}/rewards-improvement/pip/${id}`),
  approvePIP: (id) => api.post(`${HRM_BASE_URL}/rewards-improvement/pip/${id}/approve`),
  updatePIPGoal: (id, goalId, data) => api.put(`${HRM_BASE_URL}/rewards-improvement/pip/${id}/goals/${goalId}`, data),
  updatePIPMilestone: (id, milestoneId, data) => api.put(`${HRM_BASE_URL}/rewards-improvement/pip/${id}/milestones/${milestoneId}`, data),
  getPIPsByEmployee: (employeeId, params = {}) => api.get(`${HRM_BASE_URL}/rewards-improvement/pip/by-employee/${employeeId}`, { params }),

  getTrainingRecommendations: (params = {}) => api.get(`${HRM_BASE_URL}/rewards-improvement/training`, { params }),
  getTrainingRecommendationById: (id) => api.get(`${HRM_BASE_URL}/rewards-improvement/training/${id}`),
  createTrainingRecommendation: (data) => api.post(`${HRM_BASE_URL}/rewards-improvement/training`, data),
  updateTrainingRecommendation: (id, data) => api.put(`${HRM_BASE_URL}/rewards-improvement/training/${id}`, data),
  deleteTrainingRecommendation: (id) => api.delete(`${HRM_BASE_URL}/rewards-improvement/training/${id}`),
};

// ============================================================================
// PERFORMANCE DASHBOARD
// ============================================================================
export const performanceDashboardApi = {
  getAdminDashboard: (params = {}) => api.get(`${HRM_BASE_URL}/performance-dashboard/admin`, { params }),
  getSelfDashboard: (params = {}) => api.get(`${HRM_BASE_URL}/performance-dashboard/self`, { params }),
};

// ============================================================================
// COMPANY DOCUMENTS
// ============================================================================
export const companyDocumentApi = {
  getDocuments: (params = {}) => api.get(`${HRM_BASE_URL}/company-documents`, { params }),
  getDashboardDocuments: (params = {}) => api.get(`${HRM_BASE_URL}/company-documents/dashboard`, { params }),
  createDocument: (formData) => api.post(`${HRM_BASE_URL}/company-documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (id, data) => api.put(`${HRM_BASE_URL}/company-documents/${id}`, data),
  deleteDocument: (id) => api.delete(`${HRM_BASE_URL}/company-documents/${id}`),
};

// Export all APIs
export default {
  organizationApi,
  employeeApi,
  attendanceApi,
  leaveApi,
  recruitmentApi,
  performanceApi,
  payrollApi,
  hrmDashboardApi,
  employeeLifeCycleApi,
  workCalendarApi,
  reportsApi,
  fileApi,
  essApi,
  adminTicketApi,
  kpiSetupApi,
  kpiAssignmentApi,
  performanceTrackingApi,
  performanceEvaluationApi,
  performanceMgmtReportsApi,
  rewardsImprovementApi,
  performanceDashboardApi,
  companyDocumentApi,
  commonKpiApi,
};

